'use client'

import { useLanguage, type Locale } from '@/lib/i18n'
import { cn } from '@/lib/utils'

/**
 * Language toggle button — switches between EN and PT-BR.
 * Placed in the header next to the search bar.
 * 
 * Design: pill capsule with sliding indicator, using brand tokens.
 */
export function LanguageToggle() {
  const { locale, setLocale } = useLanguage()

  const options: { value: Locale; label: string; flag: string }[] = [
    { value: 'pt-BR', label: 'PT', flag: '🇧🇷' },
    { value: 'en', label: 'EN', flag: '🇺🇸' },
  ]

  return (
    <div
      className={cn(
        'relative flex items-center',
        'h-9 rounded-full',
        'border border-border/50 bg-white/50',
        'backdrop-blur-sm',
        'transition-all duration-200',
        'hover:border-brand-cornflower/40 hover:shadow-sm'
      )}
      role="radiogroup"
      aria-label="Language selection"
    >
      {options.map((opt) => {
        const isActive = locale === opt.value
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={isActive}
            aria-label={`Switch to ${opt.value === 'pt-BR' ? 'Portuguese' : 'English'}`}
            onClick={() => setLocale(opt.value)}
            className={cn(
              'relative z-10 flex items-center gap-1.5 px-3 py-1.5',
              'rounded-full text-xs font-semibold',
              'transition-all duration-200 ease-out',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower/50',
              isActive
                ? 'bg-brand-navy text-white shadow-soft'
                : 'text-muted-foreground hover:text-brand-navy'
            )}
          >
            <span className="text-sm leading-none">{opt.flag}</span>
            <span>{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}
